import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Explicit } from '../../components/ui/Explicit';
import { DrawerLink } from '../../app/DrawerLink';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';

import { Alert } from '../../components/ui/Alert';
import { Button } from '../../components/ui/Button';
import { Card, Chip } from '../../components/ui/Card';
import { ArtworkPicker } from '../../components/ui/ArtworkPicker';
import { FavoriteButton } from '../../components/ui/FavoriteButton';
import { LoadingPanel } from '../../components/ui/Loader';
import { copy } from '../../config/copy';
import { ApiError } from '../../lib/api';
import { usePlayer } from '../../stores/player';
import { AlbumEditor } from './AlbumEditor';
import { AddTracksDialog } from './AddTracksDialog';
import { albums, type AlbumDetail } from './api';
import { useDocumentTitle } from '../../lib/title';

/**
 * An album: its cover, its running order, and - to its owner - the controls
 * that change both.
 *
 * The owner's view and the visitor's view are the same page rather than two,
 * because an artist arranging a release wants to see what everybody else will
 * see. Ownership adds controls; it does not switch layouts.
 */
export function AlbumPage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [editing, setEditing] = useState(false);
  const [adding, setAdding] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  const play = usePlayer((state) => state.play);
  const current = usePlayer((state) => state.track);
  const playing = usePlayer((state) => state.playing);
  const toggle = usePlayer((state) => state.toggle);

  const { data, isPending, error } = useQuery<AlbumDetail>({
    queryKey: ['album', id],
    queryFn: () => albums.get(id),
    retry: false,
  });

  useDocumentTitle(data?.title ?? null);

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['album', id] });

    if (data?.owner) {
      void queryClient.invalidateQueries({ queryKey: ['albums', data.owner.username] });
    }
  };

  const removeTrack = useMutation({
    mutationFn: (trackId: number) => albums.removeTrack(Number(id), trackId),
    onSuccess: invalidate,
  });

  const reorder = useMutation({
    mutationFn: (trackIds: number[]) => albums.reorder(Number(id), trackIds),
    onSuccess: invalidate,
  });

  const destroy = useMutation({
    mutationFn: () => albums.remove(Number(id)),
    onSuccess: () => {
      invalidate();
      navigate(data?.owner ? `/${data.owner.username}` : '/');
    },
  });

  if (isPending) {
    return (
      <div className="mx-auto max-w-4xl px-6 py-10">
        <LoadingPanel />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="mx-auto max-w-4xl px-6 py-24 text-center">
        <h1 className="text-2xl font-semibold">Nothing here</h1>
        <p className="mt-2 text-soft">
          {error instanceof ApiError && error.status === 404
            ? 'That album does not exist, or is private.'
            : 'That album could not be loaded.'}
        </p>
      </div>
    );
  }

  /*
   * A playlist that arrived at the album URL goes to its own page.
   *
   * They are one table and one endpoint, so this route can load either - and
   * every link written before playlists had a page still points here. A replace
   * rather than a push, because the album URL is not a step anybody chose and
   * should not be somewhere the back button returns to.
   */
  if (data.type === 'playlist') {
    return <Navigate to={`/playlists/${data.id}`} replace />;
  }

  const playable = data.tracks.filter((track) => track.status === 'ready');

  /**
   * Move one track up or down, then send the whole order.
   *
   * Arrows rather than drag-and-drop, deliberately for now: dragging needs a
   * pointer, and a keyboard or touch user gets nothing from it. Two buttons
   * work everywhere, and the API takes the full list either way - so adding
   * dragging later is a change to this component and to nothing else.
   */
  const move = (index: number, direction: -1 | 1) => {
    const next = [...data.tracks];
    const target = index + direction;

    if (target < 0 || target >= next.length) return;

    [next[index], next[target]] = [next[target]!, next[index]!];

    reorder.mutate(next.map((track) => track.id));
  };

  return (
    <div className="mx-auto max-w-4xl px-6 py-10">
      <header className="flex flex-col gap-6 sm:flex-row sm:items-end">
        <ArtworkPicker
          subject="collections"
          subjectId={data.id}
          artwork={data.artwork}
          refetch={() => albums.get(data.id)}
          editable={data.is_owner}
          onUploaded={invalidate}
        />

        <div className="min-w-0 flex-1">
          <p className="meta-caps">{copy['noun.collection']}</p>
          <h1 className="mt-1 text-3xl font-semibold tracking-tight text-ink">{data.title}</h1>

          {data.owner && (
            <p className="mt-2 text-sm text-soft">
              <Link to={`/${data.owner.username}`} className="hover:underline">
                {data.owner.display_name || `@${data.owner.username}`}
              </Link>
            </p>
          )}

          <div className="mt-3 flex flex-wrap items-center gap-2">
            <span className="meta-caps">
              {data.track_count} {data.track_count === 1 ? copy['noun.track'] : copy['noun.track.plural']}
            </span>

            {/* Only ever present on your own album - the server sends `public`
                to everybody else regardless of what it really is. */}
            {data.visibility !== 'public' && (
              <Chip>{data.visibility === 'private' ? 'Private' : 'Unlisted'}</Chip>
            )}
          </div>

          {data.description && (
            <p className="mt-4 whitespace-pre-wrap text-[15px] leading-relaxed text-soft">
              {data.description}
            </p>
          )}
        </div>
      </header>

      <div className="mt-8 flex flex-wrap items-center gap-3">
        <Button
          disabled={playable.length === 0}
          onClick={() => {
            const first = playable[0];

            if (!first) return;

            // The rest of the album queues behind it, which is the entire
            // reason somebody opened an album rather than a track.
            void play(first, playable, 'collection');
          }}
        >
          Play album
        </Button>

        {data.is_owner && (
          <>
            <Button variant="secondary" onClick={() => setAdding(true)}>
              {copy['cta.addTracks']}
            </Button>
            <Button variant="ghost" onClick={() => setEditing(true)}>
              {copy['cta.editAlbum']}
            </Button>
          </>
        )}
      </div>

      {reorder.isError && (
        <div className="mt-4">
          <Alert tone="error">That reorder did not save. Try again.</Alert>
        </div>
      )}

      {/* ------------------------------------------------------------- */}
      {/* The running order                                              */}
      {/* ------------------------------------------------------------- */}
      {data.tracks.length === 0 ? (
        <Card className="mt-6 p-6">
          <p className="text-sm text-soft">
            {data.is_owner
              ? 'No tracks yet. Add some of your uploads to build the release.'
              : 'This album has no tracks yet.'}
          </p>
        </Card>
      ) : (
        <ol className="mt-6 flex flex-col gap-2" data-album-tracks>
          {data.tracks.map((track, index) => {
            const isCurrent = current?.id === track.id;
            const ready = track.status === 'ready';

            return (
              <li key={track.id} data-album-track={track.id}>
                <Card className="flex items-center gap-3 p-3 sm:gap-4 sm:p-4">
                  <span className="meta-caps w-6 shrink-0 text-right tabular-nums">{index + 1}</span>

                  <button
                    type="button"
                    disabled={!ready}
                    aria-label={isCurrent && playing ? `Pause ${track.title}` : `Play ${track.title}`}
                    onClick={() => (isCurrent ? toggle() : void play(track, playable, 'collection'))}
                    className={[
                      'flex h-10 w-10 shrink-0 items-center justify-center rounded-[var(--radius-pill)]',
                      'transition-colors disabled:cursor-not-allowed disabled:opacity-40',
                      ready ? 'bg-accent text-accent-ink hover:bg-accent-hover' : 'bg-raised text-faint',
                    ].join(' ')}
                  >
                    {isCurrent && playing ? (
                      <svg viewBox="0 0 16 16" className="h-4 w-4" fill="currentColor" aria-hidden="true">
                        <rect x="4" y="2.5" width="3" height="11" rx="1" />
                        <rect x="9" y="2.5" width="3" height="11" rx="1" />
                      </svg>
                    ) : (
                      <svg viewBox="0 0 16 16" className="h-4 w-4 translate-x-px" fill="currentColor" aria-hidden="true">
                        <path d="M4.5 2.8v10.4a.6.6 0 0 0 .93.5l8-5.2a.6.6 0 0 0 0-1l-8-5.2a.6.6 0 0 0-.93.5Z" />
                      </svg>
                    )}
                  </button>

                  <div className="min-w-0 flex-1">
                    <DrawerLink
                      to={`/tracks/${track.id}`}
                      className="block truncate text-sm font-medium text-ink hover:underline"
                    >
                      {track.explicit && <Explicit className="mr-1.5 align-[-2px]" />}
                      {track.title}
                    </DrawerLink>

                    <div className="mt-1 flex flex-wrap items-center gap-2">
                      <span className="meta-caps">{duration(track.duration_ms)}</span>
                      {track.visibility !== 'public' && (
                        <Chip>{track.visibility === 'private' ? 'Private' : 'Unlisted'}</Chip>
                      )}
                      {track.status === 'processing' && <Chip tone="signal">Processing</Chip>}
                    </div>
                  </div>

                  {ready && (
                    <FavoriteButton
                      trackId={track.id}
                      favorited={track.favorited}
                      likeCount={track.like_count}
                      size="sm"
                    />
                  )}

                  {data.is_owner && (
                    <div className="flex shrink-0 items-center gap-1">
                      <OrderButton
                        label={`Move ${track.title} up`}
                        disabled={index === 0 || reorder.isPending}
                        onClick={() => move(index, -1)}
                      >
                        <path d="M8 4l4 5H4z" />
                      </OrderButton>

                      <OrderButton
                        label={`Move ${track.title} down`}
                        disabled={index === data.tracks.length - 1 || reorder.isPending}
                        onClick={() => move(index, 1)}
                      >
                        <path d="M8 12L4 7h8z" />
                      </OrderButton>

                      <button
                        type="button"
                        aria-label={`Remove ${track.title} from this album`}
                        title="Remove from album"
                        disabled={removeTrack.isPending}
                        onClick={() => removeTrack.mutate(track.id)}
                        className="flex h-8 w-8 items-center justify-center rounded-[var(--radius-pill)] text-soft transition-colors hover:bg-raised hover:text-hot disabled:opacity-40"
                      >
                        <svg viewBox="0 0 16 16" className="h-4 w-4" fill="currentColor" aria-hidden="true">
                          <path d="M3 7h10v2H3z" />
                        </svg>
                      </button>
                    </div>
                  )}
                </Card>
              </li>
            );
          })}
        </ol>
      )}

      {/*
        Said where the button is, because it is the thing somebody worries
        about before clicking either one.
      */}
      {data.is_owner && data.tracks.length > 0 && (
        <p className="mt-3 text-xs text-faint">
          Removing a track here takes it off the album. It stays on your profile and keeps its own page.
        </p>
      )}

      {/* ------------------------------------------------------------- */}
      {/* Deleting                                                       */}
      {/* ------------------------------------------------------------- */}
      {data.is_owner && (
        <div className="mt-12 border-t border-line pt-6">
          {confirmingDelete ? (
            <div className="flex flex-col gap-3">
              <Alert tone="error">
                Delete “{data.title}”? The {data.track_count}{' '}
                {data.track_count === 1 ? 'track' : 'tracks'} on it are not deleted — they stay on your
                profile. This cannot be undone.
              </Alert>
              <div className="flex gap-3">
                <Button variant="danger" loading={destroy.isPending} onClick={() => destroy.mutate()}>
                  Delete album
                </Button>
                <Button variant="ghost" onClick={() => setConfirmingDelete(false)}>
                  Keep it
                </Button>
              </div>
            </div>
          ) : (
            <Button variant="ghost" onClick={() => setConfirmingDelete(true)}>
              Delete this album
            </Button>
          )}
        </div>
      )}

      {editing && (
        <AlbumEditor
          album={data}
          onClose={() => setEditing(false)}
          onSaved={() => {
            setEditing(false);
            invalidate();
          }}
        />
      )}

      {adding && data.owner && (
        <AddTracksDialog
          albumId={data.id}
          username={data.owner.username}
          alreadyOn={new Set(data.tracks.map((track) => track.id))}
          onClose={() => setAdding(false)}
          onAdded={invalidate}
        />
      )}
    </div>
  );
}

function OrderButton({
  label,
  disabled,
  onClick,
  children,
}: {
  label: string;
  disabled: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      className="flex h-8 w-8 items-center justify-center rounded-[var(--radius-pill)] text-soft transition-colors hover:bg-raised hover:text-ink disabled:opacity-30"
    >
      <svg viewBox="0 0 16 16" className="h-4 w-4" fill="currentColor" aria-hidden="true">
        {children}
      </svg>
    </button>
  );
}

/** `4:07`, or an em dash while the duration is still unknown. */
function duration(ms: number | null): string {
  if (ms === null) return '—';

  const total = Math.round(ms / 1000);

  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
}
